import type { Application } from '@/api/types'
import { hashId } from '@/shared/lib/hash-id'

/**
 * Cache updaters for one workspace's applications.
 *
 * Ported from the `force*` actions in
 * `web-frontend/modules/core/store/application.js`. Same immutability contract
 * as the workspace updaters: a new array only when something changed.
 */

export function addApplication(
  applications: Application[],
  application: Application,
): Application[] {
  const index = applications.findIndex((existing) => existing.id === application.id)
  if (index === -1) return [...applications, application]

  // Upstream replaces rather than appends here too: a create can arrive after
  // the list that already contains it, and a workspace restore sends every
  // application at once.
  const next = [...applications]
  next[index] = application
  return next
}

export function addApplications(
  applications: Application[],
  incoming: Application[],
): Application[] {
  return incoming.reduce(addApplication, applications)
}

export function updateApplication(
  applications: Application[],
  applicationId: number,
  values: Partial<Application>,
): Application[] {
  const index = applications.findIndex((application) => application.id === applicationId)
  if (index === -1) return applications

  const next = [...applications]
  // `Application` is a discriminated union over `type`, and spreading a union
  // with a `Partial` of it loses the discriminant. The values come from the
  // server's own serialisation of the same application, so the member does not
  // change; only TypeScript's ability to see that does.
  next[index] = { ...applications[index]!, ...values } as Application
  return next
}

export function removeApplication(
  applications: Application[],
  applicationId: number,
): Application[] {
  const next = applications.filter((application) => application.id !== applicationId)
  return next.length === applications.length ? applications : next
}

/**
 * Renumbers `order` from a list of *hashed* application ids.
 *
 * See `hashId` for why the server does not send the ids themselves.
 *
 * An application the ordering does not mention keeps the order it had. Upstream
 * assigns it `undefined`, which is a hole in a field the interface sorts on.
 */
export function orderApplications(
  applications: Application[],
  hashedOrder: string[],
): Application[] {
  return applications.map((application) => {
    const index = hashedOrder.indexOf(hashId(application.id))
    return index === -1 ? application : { ...application, order: index + 1 }
  })
}
